import { Box, Button, Icon, Popover, Stack, Text } from '@sixfold/common-ui';
import React, { useCallback } from 'react';
import { NavLink, Route, Routes as RouterRoutes } from 'react-router';

import { ThemeSwitcher } from './theme_switcher';
import { CompaniesContainer } from '../../company';
import { useTimeZone } from '../../components/date_formatting/timezone_context';
import { HomeContainer } from '../../home/components/home';
import { KPIContainer } from '../../kpi/kpi_container';
import { MiscellaneousContainer } from '../../miscellaneous/containers/miscellaneous_container';
import { MobileCommandCenter } from '../../mobile_command_center';
import { Routes, withTitle } from '../../routes';
import { TelematicsContainer } from '../../telematic_integration';
import { ApiUsageContainer } from '../../telematic_integration/containers/api_usage_container';
import { ToursContainer } from '../../tour';
import { UsersContainer } from '../../user';
import { VehiclesContainer } from '../../vehicle';

import styles from './workspace.module.css';

const intranetDocumentationLink =
  'https://www.notion.so/transporeon/Backoffice-General-info-07ff3830fa6745808bca3d09d8ecc926?pvs=4#16fc1b3dd256802b9244d573410d2a3e';
const intranetQuestionsLink =
  'https://www.notion.so/transporeon/Questions-and-feedback-Backoffice-documentation-Visibility-Hub-d02566b041aa40eaa98757853496cf77';

interface Props {
  onSignout(): void;
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink;

const NavigationBar: React.FC<{ onSignout: () => void }> = ({ onSignout }) => {
  const { timeZone, setTimeZone } = useTimeZone();
  const toggleDateFormat = useCallback(() => {
    const updatedTimeZone = timeZone === 'UTC' ? 'local' : 'UTC';
    setTimeZone(updatedTimeZone);
    localStorage.setItem('timeZone', updatedTimeZone);
  }, [setTimeZone, timeZone]);

  return (
    <nav className={styles.menu}>
      <Box paddingInlineEnd="medium">
        <NavLink to={Routes.Home.generatePath({})} end className={styles.logo}>
          <Stack direction="row" alignItems="center" gap="small">
            <Icon name="logomark-6f" />
            <span className={styles.logoText}>Backoffice</span>
          </Stack>
        </NavLink>
      </Box>
      <NavLink to={Routes.Vehicles.generatePath({})} className={navLinkClass}>
        <Icon name="carriers" size={20} />
        <span>Vehicles</span>
      </NavLink>
      <NavLink to={Routes.Tours.generatePath({})} className={navLinkClass}>
        <Icon name="shipments" size={20} />
        <span>Tours</span>
      </NavLink>
      <NavLink to={Routes.MobileCommandCenter.generatePath({})} className={navLinkClass}>
        <Icon name="circle-mobile" size={20} />
        <span>Mobile</span>
      </NavLink>
      <NavLink to={Routes.Companies.generatePath({})} className={navLinkClass}>
        <Icon name="circle-shipper" size={20} />
        <span>Companies</span>
      </NavLink>
      <NavLink to={Routes.Users.generatePath({})} className={navLinkClass}>
        <Icon name="users" size={20} />
        <span>Users</span>
      </NavLink>
      <NavLink to={Routes.Telematics.generatePath({})} className={navLinkClass}>
        <Icon name="circle-signal" size={20} />
        <span>Telematics</span>
      </NavLink>
      <NavLink to={Routes.ApiUsage.generatePath({})} className={navLinkClass}>
        <Icon name="truck-eye" size={20} />
        <span>Visibility API</span>
      </NavLink>
      <NavLink to={Routes.Miscellaneous.generatePath({})} className={navLinkClass}>
        <Icon name="cog" size={20} />
        <span>Miscellaneous</span>
      </NavLink>
      <div className={styles.rightMenu}>
        <div className={styles.menuItem}>
          <ThemeSwitcher />
        </div>
        <div className={styles.menuItem}>
          <Popover
            placement="bottom-end"
            content={
              <Stack direction="column" gap="none">
                <Stack
                  alignItems="center"
                  gap="small"
                  wrap={false}
                  as="a"
                  href={intranetDocumentationLink}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  style={{ padding: '8px 12px', textDecoration: 'none', color: 'inherit', borderRadius: '4px' }}>
                  <span style={{ display: 'flex', width: 16, flexShrink: 0 }}>
                    <Icon name="small-book" size={16} />
                  </span>
                  <Text>Backoffice documentation</Text>
                </Stack>
                <Stack
                  alignItems="center"
                  gap="small"
                  wrap={false}
                  as="a"
                  href={intranetQuestionsLink}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  style={{ padding: '8px 12px', textDecoration: 'none', color: 'inherit', borderRadius: '4px' }}>
                  <span style={{ display: 'flex', width: 16, flexShrink: 0 }}>
                    <Icon name="chat" size={16} />
                  </span>
                  <Text>Questions and feedback</Text>
                </Stack>
              </Stack>
            }>
            <Button kind="ghost" size="small" hideLabel iconStart="small-help">
              Help
            </Button>
          </Popover>
        </div>
        <div className={styles.menuItem}>
          <Button
            onClick={toggleDateFormat}
            kind="ghost"
            size="small"
            iconStart={timeZone === 'UTC' ? 'small-earth' : 'small-circular-clock'}>
            {timeZone === 'UTC' ? 'UTC' : 'Local'} time
          </Button>
        </div>
        <div className={styles.menuItem}>
          <Button kind="ghost" size="small" iconStart="small-logout" onClick={onSignout}>
            Logout
          </Button>
        </div>
      </div>
    </nav>
  );
};

export const Workspace: React.FC<Props> = (props) => {
  return (
    <div className="workspace">
      <NavigationBar onSignout={props.onSignout} />
      <Box paddingBlockStart="medium">
        <RouterRoutes>
          <Route index element={<TitledHomeContainer />} />
          <Route path={Routes.KPI.relativePath} element={<KPIContainer />} />
          <Route path={Routes.Vehicles.relativePath} element={<TitledVehiclesContainer />} />
          <Route path={Routes.Tours.relativePath} element={<TitledToursContainer />} />
          <Route path={Routes.MobileCommandCenter.relativePath} element={<TitledMobileCommandCenter />} />
          <Route path={Routes.Users.relativePath} element={<TitledUsersContainer />} />
          <Route path={Routes.Companies.relativePath} element={<TitledCompaniesContainer />} />
          <Route path={Routes.Telematics.relativePath} element={<TitledTelematicsContainer />} />
          <Route path={Routes.ApiUsage.relativePath} element={<TitledApiUsageContainer />} />
          <Route path={Routes.Miscellaneous.relativePath} element={<TitledMiscellaneousContainer />} />
        </RouterRoutes>
      </Box>
    </div>
  );
};

const TitledHomeContainer = withTitle('Home', HomeContainer);
const TitledVehiclesContainer = withTitle('Vehicles', VehiclesContainer);
const TitledToursContainer = withTitle('Tours', ToursContainer);
const TitledMobileCommandCenter = withTitle('Mobile Command Center', MobileCommandCenter);
const TitledUsersContainer = withTitle('Users', UsersContainer);
const TitledCompaniesContainer = withTitle('Companies', CompaniesContainer);
const TitledTelematicsContainer = withTitle('Telematics', TelematicsContainer);
const TitledApiUsageContainer = withTitle('Visibility API', ApiUsageContainer);
const TitledMiscellaneousContainer = withTitle('Miscellaneous', MiscellaneousContainer);
